import React, { useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import Navbar from '../components/Cripsr/NavbarCrispr';
import PhoneSlide from '../components/phones/PhoneSlide';

import anim1 from '../assets/lottie/MartaWaitingRoom.json';
import anim2 from '../assets/lottie/MartaResearch.json';
import anim3 from '../assets/lottie/MartaPhoneHand.json';
import anim4 from '../assets/lottie/MartaPlanets.json';
import anim6 from '../assets/lottie/Marta Ending.json';


import ArticleIntro from '../components/phones/ArticleIntro';
import NarrativeSection from "../components/phones/NarrativeSection";

import martaImg from "../assets/images/VolunteerProfiles-01.png";
import siliconThumb from "../assets/images/hero/Hero Images-02.png";

import relationshipsHero from "../assets/images/Volunteer Profiles-02.png";
import friendOrFoeThumb from "../assets/images/hero/Hero Images-04.png";

import privacyHero from "../assets/images/Volunteer Profiles-03.png";
import privacyThumb from "../assets/images/hero/Hero Images-01.png";

import mentalHealthHero from "../assets/images/Volunteer Profiles-04.png"
import gutCheckThumb from "../assets/images/hero/Hero Images-03.png"

import mindfulnessHero from "../assets/images/Volunteer Profiles-05.png";
import rechargingThumb from "../assets/images/hero/Hero Images-05.png";
import ArticleCredits from '../components/phones/ArticleCredits';


import EndOfStoryNav from "../components/phones/EndOfStoryNav";
// images
import nextSiliconBanner from "../assets/images/hero/Hero Images-02.png";
import foeThumb from "../assets/images/hero/Hero Images-04.png";

import gutThumb from "../assets/images/hero/Hero Images-03.png";
import BecauseScienceFooter from '../components/phones/BecauseScienceFooter';



const navLinks = [
    { label: "We love our phones", href: "/phones" },
    { label: "From stone to silicon", href: "/phones/stoneandsilicon" },
    { label: "Friend or foe", href: "/phones/friendorfoe" },
    { label: "The privacy burden", href: "/phones/privacyburden" },
    { label: "Gut check", href: "/phones/gutcheck" },
    { label: 'Recharging', href: '/phones/recharging' },
];

const slides = [
    {
        text: "On a recent Tuesday, Marta, 70, sat waiting in her doctor’s office phone in hand, swiping through the day’s updates.",
        anim: anim1
    },
    {
        text: "A new art gallery opening near her friend in Canada; a comment on a picture of her newborn granddaughter in Ecuador; updates on her girlfriend's flirtatious encounter with a barista in Colombia.",
        anim: anim2
    },
    {
        text: "At all hours of the day, her phone buzzes and vibrates, serving as the mobile connection point between Marta and her half dozen girlfriends around the world.",
        anim: anim3
    },
    {
        text: "“Everyday I get at least 20 messages from them,” said Marta, chuckling. “We talk about everything… we just share.”",
        anim: anim4
    },
    {
        text: "She feels more connected than ever. Yet studies show that the urge to keep up with constant news can lead to stress, anxiety and worsening physical health.",
        anim: anim2
    },
    {
        text: "Marta says the constant notifications and information doesn’t bother her… but science tells us otherwise.",
        anim: anim6
    }

];

const PhonesPage = () => {
    const [scrollIndex, setScrollIndex] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY;
            const screenHeight = window.innerHeight;
            const index = Math.floor(scrollTop / screenHeight);
            setScrollIndex(index);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="relative w-full">
            {/* Navbar */}
            <Navbar isShrunk={scrollIndex > 0} navLinks={navLinks} />

            {/* Phone Slides Section */}
            <div className="relative h-[600vh]">
                {/* Lottie Background (only active during slide zone) */}
                <div className="fixed inset-0 z-0 pointer-events-none flex items-center justify-center mt-16">
                    <div className="w-[800px] max-w-[80vw]">
                        <Lottie animationData={slides[scrollIndex]?.anim} loop autoplay />
                    </div>
                </div>

                {/* Text Slides */}
                <div className="relative z-10">
                    {slides.map((slide, idx) => (
                        <PhoneSlide key={idx} text={slide.text} />
                    ))}
                </div>
            </div>

            {/* Article Section */}
            <div className="relative z-10">
                <ArticleIntro />
                <NarrativeSection
                    id="shared-evolution"
                    containerWidth="max-w-md"
                    padY="py-10"
                    centeredImage={{ src: martaImg, alt: "Marta illustration", widthClass: "w-[50%] sm:w-[60%]" }}
                    heading="Part of our shared evolution"
                    content={[
                        `Since the dawn of humans, we have been defined by three things: walking upright, having large brains and developing and using tools. No other animal that we share the planet with has these three attributes. It is what makes us distinctive, what has helped us survive and evolve, and what has led to the Anthropocene.`,
                        `But how did we get from stone tools to smartphones and is the advancement of these slick, shiny devices just the next step in our evolution or are they the beginning of the end? Do they advance civilization or do they induce behaviors that are more dangerous than adaptive?`,
                        `Evolutionary anthropology can provide clues.`,
                        `When you look at stone axes and smartphones, you can see how far tools have come. Using objects to advance self interests – whether for chopping meat in 2000 BC or connecting us with friends in 2023 – is rooted in evolutionary behavior. Even the use of social media, experts say, has links to prehistoric times.`,
                        `“What we’re seeing with social media today is an effect of something that’s been going on for 8 million years,” says Katie Ranhron, an archaeologist who studies the evolution of human behavior, focusing on material culture, stone artifacts and beads.`,
                        `“We see remnants of storytelling in cave art and smartphones feed our evolutionary desire to tell stories and the use of art to convey socially complex ideas.”`,
                        // pull-quote appears right here
                        {
                            type: "quote",
                            content:
                                "What we’re seeing with social media today is an effect of something that’s been going on for 8 million years",
                            tone: "primary",
                            align: "left",
                        },
                        <span key="erectus">
                            “If <em>homo erectus</em> did not have hand-axe technology, would it have been able to survive? The answer is yes, because this was one part of their adaptive strategy. But for modern humans today, we’ve gotten ourselves into a situation where we’re very dependent on technology.”
                        </span>,
                    ]}
                    skipTo={{
                        href: "/phones/stoneandsilicon",
                        label: "From stone to silicon",
                        thumbSrc: siliconThumb,
                        thumbAlt: "Stone tools thumbnail",
                    }}
                />


                <NarrativeSection
                    id="relationships"
                    containerWidth="max-w-md"
                    padY="py-10"
                    centeredImage={{
                        src: relationshipsHero,
                        alt: "Phones impacting our relationships",
                        widthClass: "w-[50%] sm:w-[60%]",
                    }}
                    heading="Phones impacting our relationships"
                    headingClass="text-blue-700 text-2xl sm:text-5xl "
                    content={[
                        `Kaya, 15, is a creature of habit.`,
                        `Each morning her alarm goes off at 6:30 a.m., sometimes it’s a soothing chime. Often it’s just a jarring beep. A quick scan while she gets the day going: her phone lets her know she missed three snaps and that the running begins: streaks and “good mornings.”`,
                        `“By the first thing I do when I wake up and the last thing I do before I go to sleep,” Kaya says. “I check my phone because I feel like I was missing out. Snapchat for me is mostly direct messages, and I can see my friends being active because I’m always having a conversation with one right before bed.”`,
                        `Her friend and her friend have a streak of 1,073 — a scoreboard that says they have a “snap” to each other every day for nearly three years.`,
                        `“On Snapchat, you’re able to show friends and so you can just laugh. You can make new friends. It’s basically every single thing in my life and it’s fun,” she says.`,
                        `Kaya, a freshman at East Mesa High School, like many teens across the country today relies more and more on her phone for social connection — especially after COVID. It’s no wonder: among her demographic, apps have changed the rules of friendship and dating.`,
                        {
                            type: "quote",
                            content: "Two-thirds of teens say they would rather communicate with their friends online than in person",
                            tone: "primary",
                            align: "left",
                            className: "text-[18px] sm:text-lg"
                        },
                    ]}
                    skipTo={{
                        href: "/phones/friendorfoe",
                        label: "Friend or foe",
                        thumbSrc: friendOrFoeThumb,
                        thumbAlt: "Friend or foe chapter thumbnail",
                    }}
                />

                <NarrativeSection
                    id="redefining-privacy"
                    containerWidth="max-w-md"
                    padY="py-10"
                    centeredImage={{
                        src: privacyHero,
                        alt: "Redefining privacy illustration",
                        widthClass: "w-[50%] sm:w-[60%]",
                    }}
                    heading="Redefining privacy"
                    headingClass="text-blue-700 text-2xl sm:text-5xl "
                    content={[
                        `Since middle school, Jason, 21, has always had a close stable group of friends. A self-proclaimed introvert, he says he doesn’t feel like he needs to make friends on social media, so he stays private. He has an Instagram but his page remains empty. He doesn’t use his real name or post photos, he doesn’t want to be tracked.`,
                        `“I was never one to post pictures, post stories or blogs, or any of that kind of stuff. It’s not really my thing,” said Jason, an avid consumer of online fan fiction and multiplayer video games.`,
                        `“I don’t truly trust what happens,” he adds. “When it’s on the internet, it’s there, but then it’s hard to erase. Even if you delete it someone could have a copy or still remember it and that kind of stuff.”`,
                        `Each day, more than 10 billion images are shared on social platforms, but who controls those images? Who are the gatekeepers? Who decides where they go? Who is depicted in them? Do the people in the images want their images willfully passed around by others?`,
                        `Rakibul Hasan, an engineering professor who studies online privacy and the temptations facing social media users, says that even if we are careful there are still dangers that lurk and once you post something online, the control you had over your data is gone.`,
                    ]}
                    skipTo={{
                        href: "/phones/privacyburden",
                        label: "The privacy burden",
                        thumbSrc: privacyThumb,
                        thumbAlt: "The privacy burden chapter thumbnail",
                    }}
                />

                <NarrativeSection
                    id="more-than-mental-health"
                    containerWidth="max-w-md"
                    padY="py-10"
                    centeredImage={{
                        src: mentalHealthHero,
                        alt: "More than mental health illustration",
                        widthClass: "w-[50%] sm:w-[60%]",
                    }}
                    heading="More than mental health"
                    headingClass="text-blue-700 text-2xl sm:text-5xl "
                    content={[
                        `A Thursday in July, Victor, 33, got seven hours and 56 minutes of sleep.`,
                        `He was four minutes shy of closing a neon green digital ring on his Apple Watch and his Daily Readiness score — according to his AutoSleep app — was a 92.`,
                        `“If I’m anything below an 83, I’ll try to take a nap for sure,” said Victor, a professional YouTuber who produces cooking and food-based videos. “If I’ve got a 91, I know for sure. I’m not going to take a nap today.”`,
                        `“The only thing that I obsess over is my sleep app on my watch. As soon as I wake up, I can tell based on how many stars are under readiness how I’m going to feel for the day, which is kind of shocking… it might be self-fulfilling to a degree and it probably is, but it’s just like a fortune teller.”`,
                        // 👇 Quote appears RIGHT HERE where you put it in the array
                        { type: "quote", content: "I can tell based on how many stars are under readiness how I'm going to feel for the day, which is kind of shocking…", tone: "primary", align: "left", className: "text-[18px] sm:text-lg" },
                        `Technology is changing the way we live and how we perceive the world. Our whole lives revolve around screens now at work, screens in our pocket and TV screens we watch at night for entertainment. And yet the data these screens log can also provide new info and data that allow us to better understand our habits.`,
                        `Researchers today are finding that prolonged screen time use is linked not only to psychological changes but also to physiological changes, like to our gut and ultimately to our energy levels. Screen time also has ramifications for our physical health.`,
                        `Increased screen time is prospectively associated with adiposity and type 2 diabetes in adults. Kids who get too much screen time may be more likely to have risk factors that increase their chances of type 2 diabetes, new research says.`,
                        `Researchers are now looking at how screen time is affecting us at a molecular level.`,
                        `“Most people have really only looked at how screen time affects the brain,” says Corrie Whisner, a nutrition professor. “We know there’s a strong gut-brain connection. So, we wanted to try to look at that gut side of that connection and see what might be there.”`,
                        `“This is the first study to examine the effects of high screen time at the molecular level.”`,
                    ]}
                    skipTo={{
                        href: "/phones/gutcheck",
                        label: "Gut check",
                        thumbSrc: gutCheckThumb,
                        thumbAlt: "Gut check chapter thumbnail",
                    }}
                />

                <NarrativeSection
                    id="mindfulness"
                    containerWidth="max-w-md"
                    padY="py-10"
                    centeredImage={{
                        src: mindfulnessHero,
                        alt: "Mindfulness illustration",
                        widthClass: "w-[50%] sm:w-[60%]",
                    }}
                    heading="Mindfulness may be the answer"
                    headingClass="text-blue-700 text-2xl sm:text-5xl "
                    content={[
                        `Sam, 21 and a photojournalist and dorm resident advisor, finds it nearly impossible today to disconnect from her phone.`,
                        `Between staying connected to her job managing student housing issues, to keeping up her personal brand and posting images of her photography to Twitter, to keeping up with what friends are up to on Snap and Instagram, the buzzes and pings for Sam don’t stop.`,
                        `“I’m constantly getting notifications from different bosses. If I get a notification from (students in the dorms) saying they need help, I need to be able to respond right away,” said Sam. “Sometimes it’s too much, too many notifications, too many emails, but I feel like I can’t not look at it.”`,
                        `“If I just lived on an island far, far away with no job and no worries, I could probably go without my phone and my (Apple) watch, but I think because of work, I obsessively check my phone to make sure I didn’t miss anything.”`,
                        {
                            type: "quote",
                            content:
                                "Sometimes it's too much, too many notifications, too many emails, but I feel like I can't not look at it.",
                            tone: "primary",
                            align: "left",
                            className: "text-[18px] sm:text-lg"
                        },
                        `Today, our minds are flooded with information. Screen time and social media can feel overwhelming. The expectation to post, the social pressure to reply and to follow along compounds upon itself. But it doesn’t have to be that way.`,
                        `Yi-Yuan Tang, a neuroscientist and body and mind researcher, for the past 30 years has been working on research around how our brains influence our attitude, decision making, emotion and social behavior.`,
                        `“The brain is so busy, this is why we easily feel fatigue,” Tang said. “We really need to find a way to help many people to optimize their brain state. Change their baseline so they can perform much better than they believe.”`,
                        `“Mindfulness can create the initial state, but when you cannot be nonjudgmental, you need something else to change yourself, so I propose both mindfulness and bodyfullness.”`,
                    ]}
                    skipTo={{
                        href: "/phones/recharging",
                        label: "Recharging",
                        thumbSrc: rechargingThumb,
                        thumbAlt: "Recharging chapter thumbnail",
                    }}
                />
                <NarrativeSection
                    id="closing"
                    containerWidth="max-w-md"
                    padY="py-10"
                    content={[
                        `Looking around the Zoom room, Marta, wearing a white flower crown and neon green mustaché head wrap, saw her friends of more than 50 years dressed in silly outfits, mostly in virtual boxes.`,
                        `They gathered for their monthly themed chat time outside of their daily WhatsApp and Facebook communications; January’s theme: “Funny old ladies.”`,
                        `One girlfriend wore a rainbow headwrap with a gaggle of beads around her neck; another had on pink wigs, another plastic purple heart sunglasses and a tropical scarf; and another deep purple eyeshadow and rainbow hair ornaments.`,
                        `“Everyone gets dressed up and we talk for a couple of hours because people are scattered all over the world,” Marta said. “Next month we are supposed to wear pajamas, I don’t know what I’m going to do about that, but we will have fun.”`,
                        `Social media is here and is not going away. It connects us to our communities, enables us to see pictures of loved ones and allows us to share our story with others.`,
                        `Today, phones are ingrained in our education, our emotional lives and relationships. But if unchecked our ever-growing screen time can lead us into unhealthy patterns, high anxiety, depression, and our compromised privacy.`,
                        `“Phones have a lot of good things, I think that made things so much easier now,” said Marta, who advocates for carrying her phone at all times in case of emergencies and also enjoys scrolling to see what her friends post on Facebook at least once a day.`,
                        `Phones themselves are not good or bad, rather it is our choice and individual responsibility to navigate how, when and for how long we use them.`,
                        `“I think phones are great, I love them,” said Marta. “But kids, they are spending so much time on the phone, I think maybe it’s too much.”`,
                    ]}
                />

                <ArticleCredits
                    lines={[
                        "Written by by David Rozul",
                        "Illustrations by Alex Davi",
                        "Audio narration by Deanna Dent",
                        "Sound design by Christopher Goulet",
                    ]}
                />
                <EndOfStoryNav
                    nextChapter={{
                        href: "/phones/stoneandsilicon",
                        title: "From stone to silicon",
                        imageSrc: nextSiliconBanner,
                        imageAlt: "Stone tools banner",
                    }}
                    more={[
                        { href: "/phones/friendorfoe", title: "Friend or foe", imageSrc: foeThumb },
                        { href: "/phones/privacyburden", title: "The privacy burden", imageSrc: privacyThumb },
                        { href: "/phones/gutcheck", title: "Gut check", imageSrc: gutThumb },
                        { href: "/phones/recharging", title: "Recharging", imageSrc: rechargingThumb },
                    ]}
                />
                <BecauseScienceFooter />
            </div>
        </div>
    );

};

export default PhonesPage;
